import { generatePath } from '@cibo/core'
import {
  AllDetailsTable,
  BiomassBurningPanel,
  CarbonFootprint,
  CashCropPanel,
  CoverCropPanel,
  DetailEditingProvider,
  FertilizerPanel,
  GrazingPanel,
  IrrigationPanel,
  LandManagementContext,
  LivestockPanel,
  Metadata,
  NativeConversionDatePanel,
  PaginatedFieldsContext,
  TillagePanel,
  useLandReportView,
} from '@cibo/landmanager'
import {
  AuthUserPermission,
  Can,
  HttpErrorDisplay,
  Loading,
  usePermissions,
  useWindowTitle,
} from '@cibo/profile'
import { Box, Divider, Grid, Link as MUILink, Paper, Stack, Typography } from '@mui/material'
import { CDLHistory } from 'components/CDLHistory'
import { LayoutSection } from 'components/ImpactLayout'
import { LandReportActionBar } from 'components/LandReport/ActionBar'
import { MapOverview } from 'components/LandReport/MapOverview'
import { ProgramsOverview } from 'components/LandReport/ProgramsOverview'
import { SectionHeader } from 'components/LandReport/SectionHeader'
import { CARBON_LAB_ROUTES } from 'navigation/types/routes'
import { useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { LandReportSkeleton } from './LandReportSkeleton'

export const LandReport = () => {
  const { t } = useTranslation('pages/LandReport')
  const { resourceId } = useParams()
  const { filters, setFilters } = useContext(PaginatedFieldsContext)
  const { LAND_MANAGER_ROUTES } = useContext(LandManagementContext)
  const { can } = usePermissions()
  const navigate = useNavigate()
  const { field, focusYear, report } = useLandReportView({ resourceId })
  useWindowTitle(field.data?.name ? field.data?.name : t('loadingField'))

  if (
    !can({ useAny: [AuthUserPermission.DEBUG_USER, AuthUserPermission.FIELD_VIEW_DETAIL_REPORT] })
  ) {
    return <Navigate replace to={LAND_MANAGER_ROUTES.ROOT} />
  }

  const handleClickLabel = (label: string) => {
    setFilters({ ...filters, labels: !!filters.labels ? [...filters.labels, label] : [label] })
    navigate(LAND_MANAGER_ROUTES.ROOT)
  }

  if (field.isLoading) {
    return (
      <LayoutSection>
        <LandReportSkeleton />
      </LayoutSection>
    )
  }

  if (field.isError) {
    return (
      <LayoutSection flex={1}>
        <Stack flex={1} justifyContent="center" alignItems="center" spacing={1}>
          <HttpErrorDisplay
            error={field.error.response?.data}
            defaultMessage={t('defaultError')}
            statusMap={{
              403: { title: t('notAllowedTitle'), body: t('notAllowedBody') },
              404: { title: t('notFoundTitle'), body: t('notFoundBody') },
            }}
          />
        </Stack>
      </LayoutSection>
    )
  }

  if (!!field.data) {
    return (
      <DetailEditingProvider>
        <LayoutSection flex={1}>
          <Stack flex={1} spacing={3} divider={<Divider />}>
            <Metadata
              field={field.data}
              onClickGrower={console.log}
              onClickLabel={handleClickLabel}
              onClickSharedProfile={console.log}
              onClickSupplyShed={console.log}
            />

            <Box>
              <Grid spacing={3} columns={{ xs: 4, md: 8, lg: 12 }} container>
                <Grid item xs={4} md={8} lg={4}>
                  <Stack spacing={3}>
                    <MapOverview
                      field={field.data}
                      sectionHeaderProps={{ sx: { height: { xs: undefined, lg: 56 } } }}
                    />
                    <ProgramsOverview field={field.data} />
                  </Stack>
                </Grid>
                <Grid item xs={4} md={8}>
                  {!field.data.footprintResolved ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <Stack spacing={1}>
                        <Loading />
                        <Typography fontStyle="italic">{t('ciboSensing')}</Typography>
                      </Stack>
                    </Box>
                  ) : (
                    <Stack spacing={3}>
                      {!report.isLoading && (
                        <Stack spacing={2}>
                          <SectionHeader
                            title={t('carbonFootprintTitle')}
                            subtitle={t('carbonFootprintSubtitle')}
                            sx={{ height: { xs: undefined, lg: 56 } }}
                          />
                          <Paper elevation={0}>
                            <CarbonFootprint field={field} focusYear={focusYear}>
                              <Can useAny={AuthUserPermission.CARBON_LAB}>
                                <Stack justifyContent="flex-end">
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign="right"
                                  >
                                    <Trans i18nKey="pages/LandReport:carbonLabCTA">
                                      <MUILink
                                        component={Link}
                                        color="secondary"
                                        to={generatePath(CARBON_LAB_ROUTES.FIELD, {
                                          resourceId,
                                        })}
                                        underline="hover"
                                      />
                                    </Trans>
                                  </Typography>
                                </Stack>
                              </Can>
                            </CarbonFootprint>
                          </Paper>
                        </Stack>
                      )}

                      <Stack spacing={2}>
                        <SectionHeader
                          title={t('practicesTitle')}
                          subtitle={t('practicesSubtitle')}
                        />
                        <Paper elevation={0} sx={{ padding: 2 }}>
                          <Grid columns={{ xs: 1, md: 2 }} spacing={4} container>
                            <Grid item xs={1}>
                              <Stack spacing={4}>
                                <CashCropPanel />
                                <CoverCropPanel />
                                <TillagePanel />
                                <FertilizerPanel />
                              </Stack>
                            </Grid>
                            <Grid item xs={1}>
                              <Stack spacing={4}>
                                <IrrigationPanel />
                                <LivestockPanel />
                                <BiomassBurningPanel />
                                <NativeConversionDatePanel />
                                <GrazingPanel />
                              </Stack>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Stack>
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </LayoutSection>

        <Can useAny={AuthUserPermission.DEBUG_USER}>
          <LayoutSection flex={1}>
            <AllDetailsTable resourceId={resourceId} />
            <Stack direction="row">
              {[2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012].map(year => (
                <Box key={year}>
                  <Typography>{year}</Typography>
                  <CDLHistory geometry={field.data?.geometry} year={year} />
                </Box>
              ))}
            </Stack>
          </LayoutSection>
        </Can>

        <LandReportActionBar resourceId={resourceId} />
      </DetailEditingProvider>
    )
  }
  return null
}
